import React, { useState, useRef, useEffect } from 'react';
import { PortraitIcon, LandscapeIcon, LockIcon } from '../icons/Icons';

const RESOLUTION_ORDER: Record<string, number> = { '1K': 1, '2K': 2, '4K': 3 };

const isResolutionLocked = (res: string, maxResolution?: string): boolean => {
  if (!maxResolution) return false;
  return (RESOLUTION_ORDER[res] ?? 1) > (RESOLUTION_ORDER[maxResolution] ?? 1);
};

interface AspectRatioModalProps {
  isOpen: boolean;
  onSelect: (aspectRatio: string) => void;
  currentAspectRatio: string;
  currentResolution: string;
  onResolutionChange: (resolution: string) => void;
  maxResolution?: '1K' | '2K' | '4K';
  position?: 'top' | 'bottom';
  outputFormat?: 'png' | 'jpeg';
  onOutputFormatChange?: (format: 'png' | 'jpeg') => void;
}

// Mapeamento de proporções para dimensões reais por resolução (baseado na API Gemini)
// 1K = ~1024px, 2K = ~2048px, 4K = ~4096px (lado maior)
const ratioDimensionsByResolution: Record<string, Record<string, { width: number; height: number; label: string }>> = {
  '1K': {
    '9:16': { width: 768, height: 1344, label: '768×1344' },
    '2:3': { width: 832, height: 1248, label: '832×1248' },
    '3:4': { width: 864, height: 1152, label: '864×1152' },
    '4:5': { width: 896, height: 1120, label: '896×1120' },
    '1:1': { width: 1024, height: 1024, label: '1024×1024' },
    '5:4': { width: 1120, height: 896, label: '1120×896' },
    '4:3': { width: 1152, height: 864, label: '1152×864' },
    '3:2': { width: 1248, height: 832, label: '1248×832' },
    '16:9': { width: 1344, height: 768, label: '1344×768' },
    '21:9': { width: 1536, height: 672, label: '1536×672' },
  },
  '2K': {
    '9:16': { width: 1536, height: 2688, label: '1536×2688' },
    '2:3': { width: 1664, height: 2496, label: '1664×2496' },
    '3:4': { width: 1728, height: 2304, label: '1728×2304' },
    '4:5': { width: 1792, height: 2240, label: '1792×2240' },
    '1:1': { width: 2048, height: 2048, label: '2048×2048' },
    '5:4': { width: 2240, height: 1792, label: '2240×1792' },
    '4:3': { width: 2304, height: 1728, label: '2304×1728' },
    '3:2': { width: 2496, height: 1664, label: '2496×1664' },
    '16:9': { width: 2688, height: 1536, label: '2688×1536' },
    '21:9': { width: 3072, height: 1344, label: '3072×1344' },
  },
  '4K': {
    '9:16': { width: 3072, height: 5376, label: '3072×5376' },
    '2:3': { width: 3328, height: 4992, label: '3328×4992' },
    '3:4': { width: 3456, height: 4608, label: '3456×4608' },
    '4:5': { width: 3584, height: 4480, label: '3584×4480' },
    '1:1': { width: 4096, height: 4096, label: '4096×4096' },
    '5:4': { width: 4480, height: 3584, label: '4480×3584' },
    '4:3': { width: 4608, height: 3456, label: '4608×3456' },
    '3:2': { width: 4992, height: 3328, label: '4992×3328' },
    '16:9': { width: 5376, height: 3072, label: '5376×3072' },
    '21:9': { width: 6144, height: 2688, label: '6144×2688' },
  },
};

// Helper to get dimensions for current resolution
const getDimensions = (ratio: string, resolution: string) => {
  return ratioDimensionsByResolution[resolution]?.[ratio] || ratioDimensionsByResolution['1K'][ratio];
};

const portraitRatios = ["9:16", "2:3", "3:4", "4:5"];
const landscapeRatios = ["21:9", "16:9", "3:2", "4:3", "5:4"];

// "auto" = mantém a proporção da imagem enviada. Sem essa opção toda edição de
// uma foto 3:2 saía forçada em 1:1, obrigando o modelo a reenquadrar e inventar
// as bordas que faltavam — origem dos artefatos de sujeito duplicado/fantasma.
// O backend resolve 'auto' para a proporção real da imagem de origem.
export const ORIGINAL_ASPECT_RATIO = 'auto';

const formatRatioLabel = (ratio: string) => (ratio === ORIGINAL_ASPECT_RATIO ? 'Original' : ratio);

const AspectRatioButton: React.FC<{
    ratio: string, 
    resolution: string,
    isLocked: boolean, 
    isActive: boolean, 
    onClick: (ratio: string) => void,
    onMouseEnter: () => void,
    onMouseLeave: () => void,
}> = ({ ratio, resolution, isLocked, isActive, onClick, onMouseEnter, onMouseLeave }) => {
    const dimensions = getDimensions(ratio, resolution);
    return (
        <button
            onClick={() => !isLocked && onClick(ratio)}
            onMouseEnter={() => !isLocked && onMouseEnter()}
            onMouseLeave={() => !isLocked && onMouseLeave()}
            disabled={isLocked}
            type="button"
            className={`w-full px-3 py-2 text-sm text-left rounded-md flex items-center justify-between transition-colors ${
                isActive ? 'bg-[#7063f7]/30 text-snapfy-text-primary border border-[#7063f7]' : 'hover:bg-snapfy-inner border border-transparent'
            } ${isLocked ? 'cursor-not-allowed opacity-60' : ''}`}
        >
            <span className="font-medium">{ratio}</span>
            <span className={`text-xs ${isActive ? 'text-snapfy-accent-soft' : 'text-snapfy-text-secondary'}`}>
                {dimensions?.label || ''}
            </span>
            {isLocked && <LockIcon />}
        </button>
    );
};

const AspectRatioModal: React.FC<AspectRatioModalProps> = ({ isOpen, onSelect, currentAspectRatio, currentResolution, onResolutionChange, maxResolution, position = 'top', outputFormat, onOutputFormatChange }) => {
  if (!isOpen) return null;

  const [hoveredRatio, setHoveredRatio] = useState<string | null>(null);
  const displayRatio = hoveredRatio || currentAspectRatio;
  const dimensions = getDimensions(displayRatio, currentResolution);

  const positionClass = position === 'top' 
    ? "bottom-full mb-2 left-0" 
    : "top-full mt-2 left-0";

  return (
      <div className={`absolute w-[420px] max-w-[90vw] bg-snapfy-surface-hover rounded-xl shadow-2xl p-4 z-[9999] text-snapfy-text-secondary ${positionClass}`}>
        {/* Resolution Selector - Top */}
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-medium text-snapfy-text-primary">Resolução</span>
          <div className="flex gap-1">
            {(['1K', '2K', '4K'] as const).map(res => {
              const locked = isResolutionLocked(res, maxResolution);
              return (
                <button
                  key={res}
                  type="button"
                  onClick={() => !locked && onResolutionChange(res)}
                  disabled={locked}
                  title={locked ? `Disponível em plano superior` : undefined}
                  className={`px-3 py-1.5 rounded text-xs font-medium transition-all ${
                    locked
                      ? 'bg-snapfy-inner text-snapfy-text-secondary cursor-not-allowed opacity-50'
                      : currentResolution === res
                        ? 'bg-[#7063f7] text-white'
                        : 'bg-snapfy-inner text-snapfy-text-secondary hover:bg-snapfy-inner'
                  }`}
                >
                  {res}
                  {locked && <LockIcon />}
                </button>
              );
            })}
          </div>
        </div>

        {/* Output Format Toggle */}
        {onOutputFormatChange && (
          <>
            <div className="border-t border-snapfy-border my-3" />
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-snapfy-text-primary">Formato de Exportação</span>
              <div className="flex bg-snapfy-inner rounded-lg p-0.5">
                <button
                  type="button"
                  onClick={() => onOutputFormatChange('png')}
                  className={`px-3 py-1.5 rounded text-xs font-medium transition-all ${
                    (outputFormat || 'png') === 'png'
                      ? 'bg-purple-600 text-white'
                      : 'text-snapfy-text-secondary hover:text-snapfy-text-primary'
                  }`}
                >
                  PNG
                </button>
                <button
                  type="button"
                  onClick={() => onOutputFormatChange('jpeg')}
                  className={`px-3 py-1.5 rounded text-xs font-medium transition-all ${
                    outputFormat === 'jpeg'
                      ? 'bg-purple-600 text-white'
                      : 'text-snapfy-text-secondary hover:text-snapfy-text-primary'
                  }`}
                >
                  JPG
                </button>
              </div>
            </div>
          </>
        )}

        {/* Divider */}
        <div className="border-t border-snapfy-border my-3" />

        {/* Original — preserva o enquadramento da imagem enviada */}
        <button
          type="button"
          onClick={() => onSelect(ORIGINAL_ASPECT_RATIO)}
          onMouseEnter={() => setHoveredRatio(ORIGINAL_ASPECT_RATIO)}
          onMouseLeave={() => setHoveredRatio(null)}
          className={`w-full px-3 py-2 mb-3 text-sm text-left rounded-md flex items-center justify-between transition-colors ${
            currentAspectRatio === ORIGINAL_ASPECT_RATIO
              ? 'bg-[#7063f7]/30 text-snapfy-text-primary border border-[#7063f7]'
              : 'hover:bg-snapfy-inner border border-transparent'
          }`}
        >
          <span className="font-medium">Original</span>
          <span className={`text-xs ${currentAspectRatio === ORIGINAL_ASPECT_RATIO ? 'text-snapfy-accent-soft' : 'text-snapfy-text-secondary'}`}>
            mesma proporção da imagem enviada
          </span>
        </button>

        {/* Aspect Ratios Grid */}
        <div className="grid grid-cols-3 gap-3">
          {/* Portrait */}
          <div>
            <div className="flex items-center gap-1 mb-2 text-xs text-snapfy-text-secondary">
              <PortraitIcon />
              <span>Portrait</span>
            </div>
            <div className="space-y-1">
              {portraitRatios.map(ratio => (
                <AspectRatioButton key={ratio} ratio={ratio} resolution={currentResolution} isLocked={false} isActive={currentAspectRatio === ratio} onClick={onSelect} onMouseEnter={() => setHoveredRatio(ratio)} onMouseLeave={() => setHoveredRatio(null)} />
              ))}
            </div>
          </div>

          {/* Square */}
          <div>
            <div className="flex items-center gap-1 mb-2 text-xs text-snapfy-text-secondary">
              <span>■</span>
              <span>Square</span>
            </div>
            <div className="space-y-1">
              <AspectRatioButton ratio="1:1" resolution={currentResolution} isLocked={false} isActive={currentAspectRatio === '1:1'} onClick={() => onSelect('1:1')} onMouseEnter={() => setHoveredRatio('1:1')} onMouseLeave={() => setHoveredRatio(null)} />
            </div>
            {/* Preview */}
            <div className="mt-3 p-2 bg-black/30 rounded border border-snapfy-border text-center">
              <div className="text-snapfy-text-primary font-medium">{formatRatioLabel(displayRatio)}</div>
              <div className="text-snapfy-accent-soft text-xs">
                {displayRatio === ORIGINAL_ASPECT_RATIO ? 'igual à origem' : dimensions?.label}
              </div>
            </div>
          </div>

          {/* Landscape */}
          <div>
            <div className="flex items-center gap-1 mb-2 text-xs text-snapfy-text-secondary">
              <LandscapeIcon />
              <span>Landscape</span>
            </div>
            <div className="space-y-1">
              {landscapeRatios.map(ratio => (
                <AspectRatioButton key={ratio} ratio={ratio} resolution={currentResolution} isLocked={false} isActive={currentAspectRatio === ratio} onClick={onSelect} onMouseEnter={() => setHoveredRatio(ratio)} onMouseLeave={() => setHoveredRatio(null)} />
              ))}
            </div>
          </div>
        </div>
      </div>
  );
};

interface AspectRatioSelectorProps {
    aspectRatio: string;
    onAspectRatioChange: (newRatio: string) => void;
    resolution?: string;
    onResolutionChange?: (newResolution: string) => void;
    maxResolution?: '1K' | '2K' | '4K';
    buttonClassName?: string;
    modalPosition?: 'top' | 'bottom';
    outputFormat?: 'png' | 'jpeg';
    onOutputFormatChange?: (format: 'png' | 'jpeg') => void;
}

const AspectRatioSelector: React.FC<AspectRatioSelectorProps> = ({
    aspectRatio,
    onAspectRatioChange,
    resolution: externalResolution,
    onResolutionChange: externalOnResolutionChange,
    maxResolution,
    buttonClassName,
    modalPosition = 'top',
    outputFormat,
    onOutputFormatChange
}) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [internalResolution, setInternalResolution] = useState('1K');
    const selectorRef = useRef<HTMLDivElement>(null);

    // Use external resolution if provided, otherwise use internal state
    const resolution = externalResolution ?? internalResolution;
    const onResolutionChange = externalOnResolutionChange ?? setInternalResolution;

    // Auto-downgrade if current resolution exceeds plan max
    useEffect(() => {
        if (maxResolution && isResolutionLocked(resolution, maxResolution)) {
            onResolutionChange(maxResolution);
        }
    }, [maxResolution, resolution, onResolutionChange]);

    const handleSelect = (ratio: string) => {
        onAspectRatioChange(ratio);
        setIsModalOpen(false);
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
          if (selectorRef.current && !selectorRef.current.contains(event.target as Node)) {
            setIsModalOpen(false);
          }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
          document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    return (
        <div ref={selectorRef} className="relative">
            <button
                type="button"
                onClick={() => setIsModalOpen(prev => !prev)}
                className={buttonClassName || "px-4 py-2 text-sm bg-snapfy-inner rounded-full hover:bg-snapfy-inner transition-colors flex items-center gap-2"}
            >
                {/* Mock format (design-system/index.html): "Proporção: {ratio}" + solid res pill */}
                <span className="text-sm font-medium">Proporção: {formatRatioLabel(aspectRatio)}</span>
                <span className="bg-snapfy-primary text-white text-[10px] font-bold px-1.5 py-0.5 rounded">{resolution}</span>
            </button>
            <AspectRatioModal
                isOpen={isModalOpen}
                onSelect={handleSelect}
                currentAspectRatio={aspectRatio}
                currentResolution={resolution}
                onResolutionChange={onResolutionChange}
                maxResolution={maxResolution}
                position={modalPosition}
                outputFormat={outputFormat}
                onOutputFormatChange={onOutputFormatChange}
            />
        </div>
    );
};

export default AspectRatioSelector;